/**
 * recordConsent — REK-1938
 *
 * Browser-callable wrapper for the `record-consent` edge function.
 * Replaces all direct `supabase.from('user_consents').insert(...)` and
 * `supabase.from('user_consents').update(...)` calls in the SPA.
 *
 * The edge function derives user_id from the caller's JWT — never from the
 * body — so no userId parameter is accepted here (it's also not needed:
 * the caller is always acting on their own consent record).
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type ConsentType = Database['public']['Enums']['consent_type'];

/**
 * Record an initial consent grant (e.g. terms_of_service at signup,
 * age_verification, onboarding consents).
 *
 * Idempotent: if a non-revoked row already exists for (user, consent_type),
 * the edge function returns success without creating a duplicate.
 */
export async function grantConsent(consentType: ConsentType): Promise<void> {
  const { error } = await supabase.functions.invoke('record-consent', {
    body: {
      operation: 'grant',
      consent_type: consentType,
    },
  });
  if (error) {
    throw new Error(`[record-consent] grant failed for ${consentType}: ${error.message}`);
  }
}

/**
 * Toggle an existing consent preference (e.g. analytics_tracking, email_marketing
 * in the privacy settings cards).
 *
 * Uses UPSERT on (user_id, consent_type) — no silent-failure bug.
 */
export async function toggleConsent(
  consentType: ConsentType,
  granted: boolean,
): Promise<void> {
  const { error } = await supabase.functions.invoke('record-consent', {
    body: {
      operation: 'toggle',
      consent_type: consentType,
      granted,
    },
  });
  if (error) {
    throw new Error(
      `[record-consent] toggle failed for ${consentType} (granted=${granted}): ${error.message}`,
    );
  }
}
